import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  HomeIcon, 
  ChevronRightIcon, 
  PlusIcon, 
  TrashIcon, 
  PencilIcon,
  SwatchIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import { toast } from 'react-toastify';

const GestioneCategoriePage = () => {
  const [loading, setLoading] = useState(true);
  const [categorie, setCategorie] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    colore: '#3B82F6',
    icona: '',
    ordinamento: 0,
    attiva: true
  });

  // Carica le categorie dal database
  useEffect(() => {
    fetchCategorie();
  }, []);

  const fetchCategorie = async () => {
    setLoading(true);
    try {
      const response = await api.get('/categorie-offerte', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data && response.data.success) {
        setCategorie(response.data.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento delle categorie:', error);
      toast.error('Errore nel caricamento delle categorie');
    } finally {
      setLoading(false);
    }
  };

  // Gestisce il salvataggio di una categoria
  const handleSave = async () => {
    if (!formData.nome.trim()) {
      toast.error('Il nome della categoria è obbligatorio');
      return;
    }

    try {
      const url = editingCategoria 
        ? `/api/categorie-offerte/${editingCategoria.id}`
        : '/api/categorie-offerte';
      
      const method = editingCategoria ? 'put' : 'post';
      
      const response = await axios[method](url, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data && response.data.success) {
        toast.success(editingCategoria ? 'Categoria aggiornata' : 'Categoria creata');
        fetchCategorie();
        handleCloseModal();
      }
    } catch (error) {
      console.error('Errore nel salvataggio della categoria:', error);
      toast.error(error.response?.data?.error || 'Errore nel salvataggio');
    }
  };

  // Gestisce l'eliminazione di una categoria
  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa categoria?')) {
      return;
    }

    try {
      const response = await api.delete(`/categorie-offerte/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data && response.data.success) {
        toast.success('Categoria eliminata');
        fetchCategorie();
      }
    } catch (error) {
      console.error('Errore nell\'eliminazione della categoria:', error);
      toast.error(error.response?.data?.error || 'Errore nell\'eliminazione');
    }
  };

  // Gestisce il riordinamento delle categorie
  const handleReorder = async (categoriaId, direzione) => {
    const currentIndex = categorie.findIndex(c => c.id === categoriaId);
    if (currentIndex === -1) return;

    let newIndex;
    if (direzione === 'su' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direzione === 'giu' && currentIndex < categorie.length - 1) {
      newIndex = currentIndex + 1;
    } else {
      return;
    }

    const newCategorie = [...categorie];
    [newCategorie[currentIndex], newCategorie[newIndex]] = [newCategorie[newIndex], newCategorie[currentIndex]];
    
    // Aggiorna gli ordinamenti
    const riordinamenti = newCategorie.map((cat, index) => ({
      id: cat.id,
      ordinamento: index
    }));

    try {
      await api.post('/categorie-offerte/riordina', { riordinamenti }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setCategorie(newCategorie);
      toast.success('Ordine aggiornato');
    } catch (error) {
      console.error('Errore nel riordinamento:', error);
      toast.error('Errore nel riordinamento');
    }
  };

  const handleOpenModal = (categoria = null) => {
    if (categoria) {
      setEditingCategoria(categoria);
      setFormData({
        nome: categoria.nome,
        descrizione: categoria.descrizione || '',
        colore: categoria.colore || '#3B82F6',
        icona: categoria.icona || '',
        ordinamento: categoria.ordinamento || 0,
        attiva: categoria.attiva
      });
    } else {
      setEditingCategoria(null);
      setFormData({
        nome: '',
        descrizione: '',
        colore: '#3B82F6',
        icona: '',
        ordinamento: categorie.length,
        attiva: true
      });
    }
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingCategoria(null);
    setFormData({
      nome: '',
      descrizione: '',
      colore: '#3B82F6',
      icona: '',
      ordinamento: 0,
      attiva: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header con breadcrumb */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/nlt" className="flex items-center hover:text-blue-600">
              <HomeIcon className="h-4 w-4 mr-1" />
              Dashboard
            </Link>
            <ChevronRightIcon className="h-4 w-4" />
            <span className="text-gray-900 font-medium">Gestione Categorie</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Titolo e pulsante aggiungi */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestione Categorie</h1>
            <p className="text-gray-600 mt-2">
              Gestisci le categorie per organizzare le offerte vetture
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nuova Categoria
          </button>
        </div>

        {/* Lista categorie */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Categorie ({categorie.length})
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {categorie.map((categoria, index) => (
              <div key={categoria.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div 
                      className="w-6 h-6 rounded-full border-2"
                      style={{ backgroundColor: categoria.colore }}
                    ></div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {categoria.nome}
                      </h3>
                      {categoria.descrizione && (
                        <p className="text-sm text-gray-600">
                          {categoria.descrizione}
                        </p>
                      )}
                      <div className="flex items-center mt-1 space-x-4 text-xs text-gray-500">
                        <span>Ordine: {categoria.ordinamento}</span>
                        <span>Offerte: {categoria.numeroOfferte || 0}</span>
                        <span className={`px-2 py-1 rounded-full ${
                          categoria.attiva 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {categoria.attiva ? 'Attiva' : 'Disattiva'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Riordinamento */}
                    <button
                      onClick={() => handleReorder(categoria.id, 'su')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    >
                      <ArrowUpIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleReorder(categoria.id, 'giu')}
                      disabled={index === categorie.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    >
                      <ArrowDownIcon className="h-4 w-4" />
                    </button>
                    
                    {/* Modifica */}
                    <button
                      onClick={() => handleOpenModal(categoria)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    
                    {/* Elimina */}
                    <button
                      onClick={() => handleDelete(categoria.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {categorie.length === 0 && (
              <div className="px-6 py-12 text-center">
                <SwatchIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  Nessuna categoria
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Inizia creando la tua prima categoria per le offerte.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modale Aggiungi/Modifica */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {editingCategoria ? 'Modifica Categoria' : 'Nuova Categoria'}
                  </h3>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Nome *
                    </label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Nome della categoria"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Descrizione
                    </label>
                    <textarea
                      value={formData.descrizione}
                      onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Descrizione della categoria"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Colore
                    </label>
                    <div className="mt-1 flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.colore}
                        onChange={(e) => setFormData({...formData, colore: e.target.value})}
                        className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.colore}
                        onChange={(e) => setFormData({...formData, colore: e.target.value})}
                        className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="#3B82F6"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.attiva}
                        onChange={(e) => setFormData({...formData, attiva: e.target.checked})}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Categoria attiva</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSave}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <CheckIcon className="h-5 w-5 mr-2" />
                  {editingCategoria ? 'Aggiorna' : 'Crea'}
                </button>
                <button
                  onClick={handleCloseModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestioneCategoriePage; 